import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
    Table,
    Button,
    Typography,
    message,
    Tag,
    Select,
    Modal,
    Form,
    Dropdown,
    Image,
    Space,
    Card,
    Tooltip,
    Spin,
    Input,
    Row,
    Col,
    Alert
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    PlusOutlined,
    MoreOutlined,
    PictureOutlined,
    DragOutlined,
    SaveOutlined,
    CloseOutlined,
    SelectOutlined,
    UploadOutlined,
    FileTextOutlined,
    CopyOutlined,
    FilterOutlined,
    SearchOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';
import useColumnVisibility from '../Common/ColumnVisibilityControl';
import QuickActionsToolbar from '../Common/QuickActionsToolbar';
import env from '../../env';
import { lotStorage, auctionStorage } from '../../utils/storage';
import NoAuctionsFound from './NoAuctionsFound';
import { DuplicateLotModal } from './LotForm';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getNumberRangeColumnProps } from '../../utils/tableFilterHelpers';
import useIsMobile from '../../hooks/useIsMobile';

const { Title, Text } = Typography;
const { Option } = Select;

const MyLots = () => {
    const [lots, setLots] = useState([]);
    const [filteredLots, setFilteredLots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [auctions, setAuctions] = useState([]);
    // Controls the "Filter by Auction" dropdown; also used to scope the drag-to-reorder
    // feature to a single auction. Seeded from localStorage/URL so deep links keep working.
    const [auctionFilterValue, setAuctionFilterValue] = useState(() => {
        const saved = localStorage.getItem('selectedAuctionId');
        return saved ? [parseInt(saved)] : null;
    });
    const [searchText, setSearchText] = useState('');
    const [activeSearchText, setActiveSearchText] = useState('');
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [reorderLots, setReorderLots] = useState([]);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    const [isCreateLotModalVisible, setIsCreateLotModalVisible] = useState(false);
    const [isAuctionSelectionModalVisible, setIsAuctionSelectionModalVisible] = useState(false);
    const [selectedAuctionForLot, setSelectedAuctionForLot] = useState(null);
    const [lotCreationType, setLotCreationType] = useState(null); // 'single' or 'bulk'
    const [duplicateLotModalVisible, setDuplicateLotModalVisible] = useState(false);
    const [lotToDuplicate, setLotToDuplicate] = useState(null);
    const [duplicateLotTitle, setDuplicateLotTitle] = useState('');
    const [duplicateLotSubmitting, setDuplicateLotSubmitting] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { currentUser } = useSelector((state) => state.user);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (currentUser?.has_auctions) {
            loadInitialData();
            loadLots();
        } else {
            setLoading(false);
        }
    }, [currentUser?.has_auctions]);

    // Keep an auction_id URL param (e.g. from "Manage Lots" on My Auctions) applied as
    // the Auction column filter so deep links still land pre-filtered.
    useEffect(() => {
        const auctionIdFromUrl = searchParams.get('auction_id');
        if (auctionIdFromUrl) {
            const auctionId = parseInt(auctionIdFromUrl);
            if (auctionId && !isNaN(auctionId)) {
                setAuctionFilterValue([auctionId]);
                localStorage.setItem('selectedAuctionId', auctionId.toString());
            }
        }
    }, [searchParams]);

    const loadInitialData = async () => {
        try {
            const auctionsResponse = await auctionService.getAuctionsForDropdown();

            if (auctionsResponse?.data) {
                setAuctions(auctionsResponse.data);
                auctionStorage.saveAuctions(auctionsResponse.data);
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
            // Try to load from cache if API fails
            const cachedAuctions = auctionStorage.getAuctions();
            if (cachedAuctions.length > 0) {
                setAuctions(cachedAuctions);
                message.warning('Showing cached auctions data');
            } else {
                message.error('Failed to load auctions');
            }
        }
    };

    const loadLots = async () => {
        try {
            setLoading(true);

            // Load all of the user's lots across every auction in one request so the
            // table's column filters/sorting/pagination work entirely client-side.
            const response = await auctionService.getMyLots({ per_page: 1000 });

            if (response.data) {
                setLots(response.data);
                setFilteredLots(response.data);
                lotStorage.saveLots(response.data);
            } else {
                console.error('Invalid response format:', response);
                message.error('Invalid response format from server');
            }
        } catch (error) {
            console.error('Error loading lots:', error);
            // Try to load from cache if API fails
            const cachedLots = lotStorage.getLots();
            if (cachedLots.length > 0) {
                setLots(cachedLots);
                setFilteredLots(cachedLots);
                message.warning('Showing cached lots data');
            } else {
                setLots([]);
                setFilteredLots([]);
                message.error('Failed to load lots');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (auctionId, lotId) => {
        try {
            await auctionService.deleteLot(auctionId, lotId);
            message.success('Lot deleted successfully');
            // Remove from cache
            lotStorage.removeLot(lotId);
            loadLots();
        } catch (error) {
            console.error('Error deleting lot:', error);
            message.error(error.response?.data?.message || 'Failed to delete lot');
        }
    };

    const openDuplicateLotModal = (lot) => {
        const baseTitle = (lot.title || '').replace(/\s*\(Copy\)\s*$/i, '').trim();
        setLotToDuplicate(lot);
        setDuplicateLotTitle(baseTitle);
        setDuplicateLotModalVisible(true);
    };

    const handleDuplicateLot = async () => {
        const title = duplicateLotTitle.trim();
        if (!title) {
            message.error('Lot title is required');
            return;
        }

        if (!lotToDuplicate?.auction?.slug || !lotToDuplicate?.slug) {
            message.error('Unable to duplicate this lot');
            return;
        }

        try {
            setDuplicateLotSubmitting(true);
            message.loading({ content: 'Duplicating lot...', key: 'duplicate' });

            await auctionService.duplicateLot(
                lotToDuplicate.auction.slug,
                lotToDuplicate.slug,
                { title }
            );

            message.success({ content: 'Lot duplicated successfully!', key: 'duplicate' });
            setDuplicateLotModalVisible(false);
            setLotToDuplicate(null);
            setDuplicateLotTitle('');
            loadLots();
        } catch (error) {
            console.error('Error duplicating lot:', error);
            message.error({
                content: error.response?.data?.message || 'Failed to duplicate lot',
                key: 'duplicate'
            });
        } finally {
            setDuplicateLotSubmitting(false);
        }
    };

    const handleToggleStatus = async (auctionSlug, lotSlug) => {
        try {
            await auctionService.toggleLotStatus(auctionSlug, lotSlug);
            message.success('Lot status updated successfully');
            loadLots();
        } catch (error) {
            console.error('Error toggling lot status:', error);
            message.error(error.response?.data?.message || 'Failed to update lot status');
        }
    };

    const handleDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const { source, destination } = result;

        if (source.index === destination.index) {
            return;
        }

        const reordered = Array.from(reorderLots);
        const [removed] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, removed);

        setReorderLots(reordered);
    };

    const handleSaveOrder = async () => {
        if (!selectedAuctionForOrdering) {
            message.error('Auction not found');
            return;
        }

        try {
            setIsSavingOrder(true);
            const lotIds = reorderLots.map((lot, index) => ({
                id: lot.id,
                order: index + 1
            }));

            await auctionService.updateLotsOrder(selectedAuctionForOrdering.slug, lotIds);
            message.success('Lots reordered successfully');
            setIsReorderMode(false);
            setReorderLots([]);
            loadLots();
        } catch (error) {
            console.error('Error updating lots order:', error);
            message.error('Failed to save new order. Please try again.');
        } finally {
            setIsSavingOrder(false);
        }
    };

    const handleCancelReorder = () => {
        setIsReorderMode(false);
        setReorderLots([]);
    };

    const handleStartReorder = () => {
        setReorderLots(lots.filter((lot) => lot.auction?.id === singleAuctionId));
        setIsReorderMode(true);
        message.info('Drag and drop lots to reorder them. Click "Save Order" when done.');
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'default',
            active: 'green',
            sold: 'green',
            unsold: 'red',
            cancelled: 'purple',
            timeout: 'red',
            closed: 'green'
        };
        return colors[status] || 'default';
    };

    const getStatusDisplayText = (status, record) => {
        switch (status) {
            case 'cancelled':
                return 'Cancelled - Reserve Not Met';
            case 'sold':
            case 'closed':
                return 'Sold';
            case 'timeout':
                return 'Timeout';
            case 'pending':
                return 'Pending';
            case 'active':
                return 'Active';
            case 'unsold':
                return 'Unsold';
            default:
                return status.toUpperCase();
        }
    };

    const getAuctionStatusColor = (status) => {
        const colors = {
            draft: 'default',
            scheduled: 'processing',
            active: 'blue',
            completed: 'success',
            cancelled: 'error'
        };
        return colors[status] || 'default';
    };

    const getAuctionStatusLabel = (status) => {
        const labels = {
            draft: 'Draft',
            scheduled: 'Scheduled',
            active: 'Active',
            completed: 'Completed',
            cancelled: 'Cancelled'
        };
        return labels[status] || status?.toUpperCase() || 'Unknown';
    };

    // Dynamic filter option lists, built from whatever lots are actually loaded
    const lotStatusFilters = useMemo(() => {
        const seen = new Set(lots.map((lot) => lot.status).filter(Boolean));
        return Array.from(seen).map((status) => ({ text: getStatusDisplayText(status), value: status }));
    }, [lots]);

    // The Auction filter narrowed to exactly one auction unlocks the drag-to-reorder feature
    const singleAuctionId = auctionFilterValue && auctionFilterValue.length === 1 ? auctionFilterValue[0] : null;
    const selectedAuctionForOrdering = useMemo(() => {
        if (!singleAuctionId) return null;
        return lots.find((lot) => lot.auction?.id === singleAuctionId)?.auction || null;
    }, [singleAuctionId, lots]);
    const auctionLotsCount = useMemo(
        () => lots.filter((lot) => lot.auction?.id === singleAuctionId).length,
        [singleAuctionId, lots]
    );

    // "Filter by Auction" + "Search Query" are applied manually against the full lots
    // list (rather than as Table column filters), so they combine with whatever the
    // Status/Price/Bids column filters narrow down to.
    const filteredLotsForDisplay = useMemo(() => {
        let result = lots;
        if (auctionFilterValue?.length) {
            result = result.filter((lot) => auctionFilterValue.includes(lot.auction?.id));
        }
        if (activeSearchText.trim()) {
            const query = activeSearchText.trim().toLowerCase();
            result = result.filter((lot) =>
                (lot.title || '').toLowerCase().includes(query) ||
                (lot.auction?.category?.name || '').toLowerCase().includes(query)
            );
        }
        return result;
    }, [lots, auctionFilterValue, activeSearchText]);

    // Keep the CSV-export snapshot in sync whenever the manual filters change, so it
    // isn't stale until the user also touches a column filter/sorter.
    useEffect(() => {
        setFilteredLots(filteredLotsForDisplay);
    }, [filteredLotsForDisplay]);

    // Table's own column filters/sorter run on top of the manual filters above; capture
    // the resulting rows (before pagination) so CSV export reflects what's on screen.
    const handleTableChange = (_pagination, _filters, _sorter, extra) => {
        setFilteredLots(extra?.currentDataSource || filteredLotsForDisplay);
    };

    const handleAuctionFilterSelect = (value) => {
        setAuctionFilterValue(value ? [value] : null);
        setSearchText('');
        setActiveSearchText('');
        if (value) {
            localStorage.setItem('selectedAuctionId', value.toString());
        } else {
            localStorage.removeItem('selectedAuctionId');
        }
    };

    const handleSearch = () => {
        setActiveSearchText(searchText);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClearSearch = () => {
        setSearchText('');
        setActiveSearchText('');
    };

    const hasActiveSearch = activeSearchText.trim() !== '';
    const hasPendingChanges = searchText !== activeSearchText;

    // Handle CSV export
    const handleExportToCSV = () => {
        try {
            const dataToExport = filteredLots.length > 0 ? filteredLots : lots;
            
            if (dataToExport.length === 0) {
                message.warning('No lots to export');
                return;
            }
            
            // Prepare CSV headers
            const headers = [
                'Lot Number',
                'Title',
                'Auction',
                'Starting Price',
                'Reserve Price',
                'Current Price',
                'Status',
                'Bids Count',
                'Lot URL'
            ];
            
            // Prepare CSV rows
            const rows = dataToExport.map(lot => [
                lot.lot_number || '',
                lot.title || '',
                lot.auction?.title || '',
                lot.starting_price ? `PKR ${parseFloat(lot.starting_price).toFixed(2)}` : '',
                lot.reserve_price ? `PKR ${parseFloat(lot.reserve_price).toFixed(2)}` : 'No reserve',
                lot.current_price ? `PKR ${parseFloat(lot.current_price).toFixed(2)}` : 'No bids',
                lot.status || '',
                lot.bids_count || 0,
                `${env.FRONTEND_URL}/auctions/${lot.auction?.slug}/lots/${lot.slug}`
            ]);
            
            // Combine headers and rows
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(cell => {
                    // Escape commas and quotes in cell content
                    const cellStr = String(cell || '');
                    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                }).join(','))
            ].join('\n');
            
            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `my-lots-${moment().format('YYYY-MM-DD-HHmmss')}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            message.success('Lots exported successfully');
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            message.error('Failed to export lots to CSV');
        }
    };

    const handleCreateLotClick = () => {
        setIsCreateLotModalVisible(true);
    };

    const handleCreateSingleLot = () => {
        setIsCreateLotModalVisible(false);
        setLotCreationType('single');
        setIsAuctionSelectionModalVisible(true);
        setSelectedAuctionForLot(null);
    };

    const handleBulkUpload = () => {
        setIsCreateLotModalVisible(false);
        setLotCreationType('bulk');
        setIsAuctionSelectionModalVisible(true);
        setSelectedAuctionForLot(null);
    };

    const handleCancelCreateLot = () => {
        setIsCreateLotModalVisible(false);
    };

    const handleAuctionSelectionForLot = (auctionId) => {
        setSelectedAuctionForLot(auctionId);
    };

    const handleProceedToCreateLot = () => {
        if (!selectedAuctionForLot) {
            message.warning('Please select an auction first');
            return;
        }
        
        const selectedAuctionObj = auctions.find(a => a.id === selectedAuctionForLot);
        if (!selectedAuctionObj) {
            message.error('Selected auction not found');
            return;
        }
        
        setIsAuctionSelectionModalVisible(false);
        
        if (lotCreationType === 'bulk') {
            navigate(`/lots/bulk-upload?auction_slug=${selectedAuctionObj.slug}`);
        } else {
            navigate(`/lots/create?auction_slug=${selectedAuctionObj.slug}`);
        }
        
        // Reset state
        setSelectedAuctionForLot(null);
        setLotCreationType(null);
    };

    const handleCancelAuctionSelection = () => {
        setIsAuctionSelectionModalVisible(false);
        setSelectedAuctionForLot(null);
        setLotCreationType(null);
    };

    const columns = [
        // {
        //     title: 'Order',
        //     key: 'order',
        //     width: 60,
        //     fixed: 'left',
        //     render: (_, record, index) => (
        //         <div style={{ 
        //             display: 'flex', 
        //             alignItems: 'center', 
        //             justifyContent: 'center',
        //             cursor: 'grab',
        //             padding: '4px'
        //         }}>
        //             <DragOutlined style={{ color: '#999', fontSize: '16px' }} />
        //             <span style={{ marginLeft: '4px', fontSize: '12px', color: '#666' }}>
        //                 {index + 1}
        //             </span>
        //         </div>
        //     )
        // },
        {
            title: 'Lot #',
            dataIndex: 'lot_number',
            key: 'lot_number',
            width: 120,
            ...getNumberRangeColumnProps('lot_number'),
            sorter: (a, b) => (a.lot_number || 0) - (b.lot_number || 0),
            render: (lotNumber) => (
                <div style={{ 
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1890ff',
                    padding: '4px 8px',
                    background: '#f0f8ff',
                    borderRadius: '4px',
                    border: '1px solid #d6e4ff'
                }}>
                    {lotNumber ?? 'N/A'}
                </div>
            )
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 260,
            fixed: isMobile ? undefined : 'left',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
            render: (text, record) => (
                <Space>
                    <div style={{ width: 40, height: 40, borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
                        {record.image ? (
                            <Image
                                src={`${env.BACKEND_STORAGE_URL}/${record.image}`}
                                alt={text}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                preview={false}
                            />
                        ) : (
                            <div style={{
                                width: '100%',
                                height: '100%',
                                background: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <PictureOutlined style={{ fontSize: 18, color: '#999' }} />
                            </div>
                        )}
                    </div>
                    <Tooltip title={text} placement="top">
                        <a
                            href={`/auctions/${record.auction.slug}/lots/${record.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#1890ff',
                                textDecoration: 'none',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.textDecoration = 'none';
                            }}
                        >
                            {text.length > 30 ? `${text.substring(0, 20)}...` : text}
                        </a>
                    </Tooltip>
                </Space>
            )
        },
        {
            title: 'Auction',
            dataIndex: ['auction', 'title'],
            key: 'auction',
            width: 200,
            render: (text, record) => (
                <Tag>
                    <Tooltip title={text} placement="top">
                        <a 
                            href={`/auctions/${record.auction.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#1890ff',
                                textDecoration: 'none',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.textDecoration = 'none';
                            }}
                        >
                            {text.length > 25 ? `${text.substring(0, 25)}...` : text}
                        </a>
                    </Tooltip>
                </Tag>
            )
        },
        {
            title: 'Starting Price',
            dataIndex: 'starting_price',
            key: 'starting_price',
            width: 120,
            ...getNumberRangeColumnProps('starting_price'),
            sorter: (a, b) => (Number(a.starting_price) || 0) - (Number(b.starting_price) || 0),
            render: (price) => `PKR ${parseFloat(price).toFixed(2)}`
        },
        {
            title: 'Reserve Price',
            dataIndex: 'reserve_price',
            key: 'reserve_price',
            width: 150,
            ...getNumberRangeColumnProps('reserve_price'),
            sorter: (a, b) => (Number(a.reserve_price) || 0) - (Number(b.reserve_price) || 0),
            render: (price) => price ? `PKR ${parseFloat(price).toFixed(2)}` : 'No reserve'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            filters: lotStatusFilters,
            onFilter: (value, record) => record.status === value,
            render: (status, record) => {
                if (status === 'cancelled') {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                            <Tag color={getStatusColor(status)}>
                                Cancelled
                            </Tag>
                            <Text type="secondary" style={{ fontSize: '10px', lineHeight: 1.2, marginTop: 2 }}>
                                Reserve Not Met
                            </Text>
                        </div>
                    );
                }
                return (
                <Tag color={getStatusColor(status)}>
                    {getStatusDisplayText(status, record)}
                </Tag>
                );
            }
        },
        {
            title: 'Current Price',
            dataIndex: 'current_price',
            key: 'current_price',
            width: 150,
            ...getNumberRangeColumnProps('current_price'),
            sorter: (a, b) => (Number(a.current_price) || 0) - (Number(b.current_price) || 0),
            render: (price) => price ? `PKR ${parseFloat(price).toFixed(2)}` : 'No bids'
        },
        {
            title: 'Bids',
            dataIndex: 'bids_count',
            key: 'bids_count',
            width: 80,
            ...getNumberRangeColumnProps('bids_count'),
            sorter: (a, b) => (a.bids_count || 0) - (b.bids_count || 0),
            render: (count) => count || 0
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => {
                const auctionStatus = record.auction?.status;
                const isAuctionCompleted = auctionStatus === 'completed';
                const isAuctionCancelled = auctionStatus === 'cancelled';
                const isDisabled = isAuctionCompleted || isAuctionCancelled;
                
                // Debug logging
                console.log('Lot Actions Debug:', {
                    lotId: record.id,
                    lotTitle: record.title,
                    auction: record.auction,
                    auctionStatus: auctionStatus,
                    isAuctionCompleted,
                    isAuctionCancelled,
                    isDisabled,
                    fullRecord: record
                });
                
                const items = [
                    {
                        key: 'view',
                        label: 'View Lot',
                        icon: <EyeOutlined />,
                        onClick: () => window.open(`/auctions/${record.auction.slug}/lots/${record.slug}`, '_blank', 'noopener,noreferrer')
                    },
                    {
                        key: 'edit',
                        label: 'Edit Lot',
                        icon: <EditOutlined />,
                        onClick: () => navigate(`/auctions/${record.auction.slug}/lots/${record.slug}/edit`),
                        disabled: isDisabled
                    },
                    {
                        key: 'images',
                        label: 'Manage Images',
                        icon: <PictureOutlined />,
                        onClick: () => navigate(`/auctions/${record.auction.slug}/lots/${record.slug}/images`),
                        disabled: isDisabled
                    },
                    {
                        key: 'duplicate',
                        label: 'Duplicate Lot',
                        icon: <CopyOutlined />,
                        onClick: () => openDuplicateLotModal(record),
                        disabled: isDisabled
                    },
                    {
                        type: 'divider'
                    },
                    {
                        key: 'delete',
                        label: 'Delete Lot',
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => {
                            Modal.confirm({
                                title: 'Are you sure you want to delete this lot?',
                                content: 'This action cannot be undone.',
                                okText: 'Yes',
                                okType: 'danger',
                                cancelText: 'No',
                                onOk: () => handleDelete(record.auction.slug, record.slug)
                            });
                        },
                        disabled: isDisabled
                    }
                ];

                return (
                    <Dropdown
                        menu={{ items }}
                        trigger={['click']}
                    >
                        <Button icon={<MoreOutlined />} />
                    </Dropdown>
                );
            }
        }
    ];

    // Column visibility control
    const { visibleColumns, ColumnVisibilityUI } = useColumnVisibility({
        baseColumns: columns,
        storageKey: 'myLotsColumnsVisibility'
    });
    const filteredColumns = visibleColumns;

    const renderDraggableTable = () => (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="lots-table">
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                            background: snapshot.isDraggingOver ? '#f0f8ff' : 'transparent',
                            transition: 'background-color 0.2s ease'
                        }}
                    >
                        {reorderLots.map((lot, index) => (
                            <Draggable key={lot.id} draggableId={lot.id.toString()} index={index}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                            ...provided.draggableProps.style,
                                            background: snapshot.isDragging ? '#e6f7ff' : 'white',
                                            border: snapshot.isDragging ? '2px solid #1890ff' : '1px solid #f0f0f0',
                                            borderRadius: '6px',
                                            marginBottom: '8px',
                                            padding: '12px',
                                            boxShadow: snapshot.isDragging ? '0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                            transform: snapshot.isDragging ? provided.draggableProps.style?.transform : 'none'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            {/* Order/Drag Handle */}
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                width: '45px',
                                                height: '40px',
                                                background: '#f0f8ff',
                                                borderRadius: '4px',
                                                border: '1px solid #d6e4ff',
                                                cursor: 'grab'
                                            }}>
                                                <DragOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                                                <span style={{ 
                                                    marginLeft: '4px', 
                                                    fontSize: '12px', 
                                                    color: '#1890ff',
                                                    fontWeight: '600'
                                                }}>
                                                    {lot.lot_number ?? 'N/A'}
                                                </span>
                                            </div>

                                            {/* Image */}
                                            <div style={{ width: 50, height: 50, borderRadius: 4, overflow: 'hidden' }}>
                                                {lot.image ? (
                                                    <Image
                                                        src={`${env.BACKEND_STORAGE_URL}/${lot.image}`}
                                                        alt={lot.title}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        preview={false}
                                                    />
                                                ) : (
                                                    <div style={{ 
                                                        width: '100%', 
                                                        height: '100%', 
                                                        background: '#f0f0f0', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center' 
                                                    }}>
                                                        <PictureOutlined style={{ fontSize: 20, color: '#999' }} />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                                    <Text strong style={{ fontSize: '14px' }}>
                                                        <Tooltip title={lot.title} placement="top">
                                                            <a 
                                                                href={`/auctions/${lot.auction.slug}/lots/${lot.slug}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ 
                                                                    color: '#1890ff',
                                                                    textDecoration: 'none',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.textDecoration = 'underline';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.textDecoration = 'none';
                                                                }}
                                                            >
                                                                {lot.title}
                                                            </a>
                                                        </Tooltip>
                                                    </Text>
                                                    {lot.status === 'cancelled' ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                                                            <Tag color={getStatusColor(lot.status)}>
                                                                Cancelled
                                                            </Tag>
                                                            <Text type="secondary" style={{ fontSize: '10px', lineHeight: 1.2, marginTop: 2 }}>
                                                                Reserve Not Met
                                                            </Text>
                                                        </div>
                                                    ) : (
                                                    <Tag color={getStatusColor(lot.status)}>
                                                        {getStatusDisplayText(lot.status, lot)}
                                                    </Tag>
                                                    )}
                                                  
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#666' }}>
                                                    <span>Starting: PKR {parseFloat(lot.starting_price).toFixed(2)}</span>
                                                    <span>Current: {lot.current_price ? `PKR ${parseFloat(lot.current_price).toFixed(2)}` : 'No bids'}</span>
                                                    <span>Bids: {lot.bids_count || 0}</span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <Dropdown
                                                menu={{
                                                    items: (() => {
                                                        const auctionStatus = lot.auction?.status;
                                                        const isAuctionCompleted = auctionStatus === 'completed';
                                                        const isAuctionCancelled = auctionStatus === 'cancelled';
                                                        const isDisabled = isAuctionCompleted || isAuctionCancelled;
                                                        
                                                        // Debug logging
                                                        console.log('Lot Actions Debug (Drag Drop):', {
                                                            lotId: lot.id,
                                                            lotTitle: lot.title,
                                                            auction: lot.auction,
                                                            auctionStatus: auctionStatus,
                                                            isAuctionCompleted,
                                                            isAuctionCancelled,
                                                            isDisabled,
                                                            fullLot: lot
                                                        });
                                                        
                                                        return [
                                                        {
                                                            key: 'view',
                                                            label: 'View Lot',
                                                            icon: <EyeOutlined />,
                                                            onClick: () => window.open(`/auctions/${lot.auction.slug}/lots/${lot.slug}`, '_blank', 'noopener,noreferrer')
                                                        },
                                                        {
                                                            key: 'edit',
                                                            label: 'Edit Lot',
                                                            icon: <EditOutlined />,
                                                                onClick: () => navigate(`/auctions/${lot.auction.slug}/lots/${lot.slug}/edit`),
                                                                disabled: isDisabled
                                                        },
                                                        {
                                                            key: 'images',
                                                            label: 'Manage Images',
                                                            icon: <PictureOutlined />,
                                                                onClick: () => navigate(`/auctions/${lot.auction.slug}/lots/${lot.slug}/images`),
                                                                disabled: isDisabled
                                                        },
                                                        {
                                                            key: 'duplicate',
                                                            label: 'Duplicate Lot',
                                                            icon: <CopyOutlined />,
                                                            onClick: () => openDuplicateLotModal(lot),
                                                            disabled: isDisabled
                                                        },
                                                        {
                                                            type: 'divider'
                                                        },
                                                        {
                                                            key: 'delete',
                                                            label: 'Delete Lot',
                                                            icon: <DeleteOutlined />,
                                                            danger: true,
                                                            onClick: () => {
                                                                Modal.confirm({
                                                                    title: 'Are you sure you want to delete this lot?',
                                                                    content: 'This action cannot be undone.',
                                                                    okText: 'Yes',
                                                                    okType: 'danger',
                                                                    cancelText: 'No',
                                                                    onOk: () => handleDelete(lot.auction.slug, lot.slug)
                                                                });
                                                                },
                                                                disabled: isDisabled
                                                            }
                                                        ];
                                                    })()
                                                }}
                                                trigger={['click']}
                                            >
                                                <Button icon={<MoreOutlined />} size="small" />
                                            </Dropdown>
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );

    return (
        <DashboardLayout>
            <PageHeader
                title="My Lots"
                description="Manage and track your auction lots"
                buttonText={currentUser?.has_auctions ? "Create Lot" : undefined}
                buttonIcon={currentUser?.has_auctions ? <PlusOutlined /> : undefined}
                onButtonClick={currentUser?.has_auctions ? handleCreateLotClick : undefined}
            />

            {!currentUser?.has_auctions ? (
                <NoAuctionsFound 
                    title="No Auctions Found"
                    description="You need to create an auction before you can add lots."
                    buttonText="Create Your First Auction"
                />
            ) : (
                <>
                    {/* Search and Filter Bar */}
                    <Card
                        className="form-section"
                        style={{
                            marginBottom: 24,
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '32px' }}
                    >
                        <div style={{
                            borderBottom: '2px solid #f0f7ff',
                            paddingBottom: 20,
                            marginBottom: 28,
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                            margin: '-32px -32px 28px -32px',
                            padding: '24px 32px'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                }}>
                                    <FilterOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                                <div>
                                    <Typography.Title
                                        level={4}
                                        style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}
                                    >
                                        Search & Filter Lots
                                    </Typography.Title>
                                    <Typography.Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                        Search by title or category, and filter by auction to find your lots quickly
                                    </Typography.Text>
                                </div>
                            </Space>
                        </div>
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={24} md={9} lg={11} xl={14}>
                                <div style={{ marginBottom: 8 }}>
                                    <Typography.Text strong style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                                        Filter by Auction
                                    </Typography.Text>
                                </div>
                                <Select
                                    style={{ width: '100%', borderRadius: '8px' }}
                                    value={singleAuctionId || undefined}
                                    onChange={handleAuctionFilterSelect}
                                    placeholder="Select an auction to view its lots..."
                                    allowClear
                                    showSearch
                                    size="large"
                                    notFoundContent="No auctions found"
                                    filterOption={(input, option) => {
                                        const auctionId = option.value;
                                        const auction = auctions.find(a => a.id === auctionId);

                                        if (!auction) return false;

                                        const query = input.toLowerCase();
                                        const auctionTitle = auction.title?.toLowerCase() || '';
                                        const statusLabel = getAuctionStatusLabel(auction.status)?.toLowerCase() || '';

                                        return (
                                            auctionTitle.includes(query) ||
                                            statusLabel.includes(query)
                                        );
                                    }}
                                    optionLabelProp="label"
                                >
                                    {auctions.map(auction => (
                                        <Option
                                            key={auction.id}
                                            value={auction.id}
                                            label={
                                                <Space>
                                                    <span>{auction.title}</span>
                                                    <Tag color={getAuctionStatusColor(auction.status)}>
                                                        {getAuctionStatusLabel(auction.status)}
                                                    </Tag>
                                                </Space>
                                            }
                                        >
                                            <Space>
                                                <span>{auction.title}</span>
                                                <Tag color={getAuctionStatusColor(auction.status)}>
                                                    {getAuctionStatusLabel(auction.status)}
                                                </Tag>
                                            </Space>
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={24} md={10} lg={7} xl={6}>
                                <div style={{ marginBottom: 8 }}>
                                    <Typography.Text strong style={{ fontSize: '14px', color: '#262626', fontWeight: 500 }}>
                                        Search Query
                                    </Typography.Text>
                                </div>
                                <Input
                                    placeholder="Search by title or category..."
                                    prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onPressEnter={handleSearchKeyPress}
                                    allowClear
                                    size="large"
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #d9d9d9',
                                        transition: 'all 0.3s'
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={24} md={5} lg={6} xl={4}>
                                <div style={{ marginBottom: 8, opacity: 0 }}>
                                    <Typography.Text strong style={{ fontSize: '14px' }}>
                                        Action
                                    </Typography.Text>
                                </div>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleSearch}
                                    size="large"
                                    block
                                    style={{
                                        height: '44px',
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        border: 'none'
                                    }}
                                >
                                    Search
                                </Button>
                            </Col>
                        </Row>
                        {hasActiveSearch && (
                            <div style={{
                                marginTop: 24,
                                paddingTop: 20,
                                borderTop: '2px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                                background: '#fafafa',
                                padding: '16px 20px',
                                borderRadius: '8px',
                                border: '1px solid #e8e8e8'
                            }}>
                                <Space size="small" wrap>
                                    <Text strong style={{ fontSize: '14px', color: '#262626', marginRight: 4, fontWeight: 600 }}>
                                        🏷️ Active Search:
                                    </Text>
                                    <Tag
                                        closable
                                        onClose={handleClearSearch}
                                        color="blue"
                                        style={{
                                            borderRadius: 6,
                                            padding: '4px 10px',
                                            fontSize: '12px',
                                            fontWeight: 500
                                        }}
                                    >
                                        <SearchOutlined style={{ marginRight: 4 }} />
                                        "{activeSearchText}"
                                    </Tag>
                                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                                        ({filteredLotsForDisplay.length} result{filteredLotsForDisplay.length !== 1 ? 's' : ''})
                                    </Text>
                                </Space>
                            </div>
                        )}
                        {hasPendingChanges && !hasActiveSearch && (
                            <Alert
                                message={<span style={{ fontWeight: 500 }}>⚠️ Pending Changes</span>}
                                description="Click 'Search' to apply your search query"
                                type="warning"
                                showIcon
                                icon={<ExclamationCircleOutlined />}
                                style={{
                                    marginTop: 16,
                                    borderRadius: '8px',
                                    border: '1px solid #ffe58f'
                                }}
                            />
                        )}
                    </Card>

                    {/* Selected Auction Info */}
                    {selectedAuctionForOrdering && (() => {
                        const selectedAuctionObj = selectedAuctionForOrdering;
                        
                        return (
                            <div className="selected-auction-info" style={{ 
                                marginBottom: 12,
                                padding: '12px 16px',
                                background: '#f0f8ff',
                                borderRadius: '8px',
                                border: '1px solid #d6e4ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                flexWrap: 'wrap'
                            }}>
                                    <div style={{ 
                                        display: 'flex', 
                                    alignItems: 'center',
                                    gap: 12,
                                        flex: 1,
                                    minWidth: 0,
                                    flexWrap: 'wrap'
                                    }}>
                                    <Text type="secondary" style={{ fontSize: '13px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        Selected Auction:
                                    </Text>
                                        <span 
                                            style={{ 
                                                fontSize: '13px', 
                                                color: '#1890ff',
                                                fontWeight: 600,
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                maxWidth: '100%',
                                            flex: '1 1 auto',
                                            minWidth: 0
                                            }}
                                        >
                                            {selectedAuctionObj.title}
                                        </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                                        <Tag 
                                            color={getAuctionStatusColor(selectedAuctionObj.status)} 
                                            style={{ 
                                                fontSize: '12px', 
                                                margin: 0,
                                                flexShrink: 0
                                            }}
                                        >
                                            {getAuctionStatusLabel(selectedAuctionObj.status)}
                                        </Tag>
                                        {loading ? (
                                            <Tag
                                                color="blue"
                                                icon={<Spin size="small" />}
                                                style={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    margin: 0,
                                                    flexShrink: 0
                                                }}
                                            />
                                        ) : (
                                            <Tag
                                                color="blue"
                                                style={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    margin: 0,
                                                    flexShrink: 0
                                                }}
                                            >
                                                {auctionLotsCount} {auctionLotsCount === 1 ? 'Lot' : 'Lots'}
                                            </Tag>
                                        )}
                                        </div>
                                    </div>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => window.open(`/auctions/${selectedAuctionObj.slug}`, '_blank', 'noopener,noreferrer')}
                                    className="selected-auction-button"
                                    style={{ 
                                        padding: '0 8px',
                                        height: 'auto',
                                        fontSize: '12px',
                                        flexShrink: 0
                                    }}
                                >
                                    <span className="selected-auction-button-text">View Auction</span>
                                </Button>
                            </div>
                        );
                    })()}

                    {/* Column Visibility Control */}
                    {singleAuctionId && ColumnVisibilityUI}

                    {/* Quick Actions Toolbar */}
                    {singleAuctionId && (
                        <QuickActionsToolbar
                            showQuickFilters={false}
                            onExportCSV={handleExportToCSV}
                            exportDisabled={filteredLots.length === 0}
                        />
                    )}

                    {selectedAuctionForOrdering && (
                        <Card
                            className="lot-ordering-card"
                            size="small"
                            style={{
                                marginBottom: 16,
                                background: 'linear-gradient(to right, #e6f7ff, #f0f8ff)',
                                borderRadius: '8px',
                                border: '1px solid #91d5ff'
                            }}
                        >
                            <div className="lot-ordering-container" style={{ 
                                padding: '12px 16px',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 12
                            }}>
                                <Space 
                                    direction="vertical" 
                                    size={4}
                                    style={{ 
                                        flex: 1,
                                        minWidth: 0
                                    }}
                                >
                                    <Space size={8} wrap>
                                        <DragOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                                        <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>
                                            Lot Ordering
                                        </Text>
                                    </Space>
                                    <Text 
                                        type="secondary" 
                                        className="lot-ordering-description"
                                        style={{ 
                                            fontSize: '12px',
                                            display: 'block',
                                            lineHeight: '1.4'
                                        }}
                                    >
                                        {isReorderMode ? 'Drag and drop lots to reorder them' : 'Change the order of lots in this auction'}
                                    </Text>
                                </Space>
                                
                                <div className="lot-ordering-actions" style={{
                                    display: 'flex',
                                    gap: 8,
                                    flexShrink: 0
                                }}>
                                    {!isReorderMode ? (
                                        <Button 
                                            type="primary" 
                                            icon={<DragOutlined />}
                                            onClick={handleStartReorder}
                                            disabled={auctionLotsCount === 0 || isSavingOrder}
                                            size="middle"
                                            style={{
                                                borderRadius: '6px',
                                                fontWeight: 500
                                            }}
                                        >
                                            <span className="lot-ordering-button-text">Change Order</span>
                                        </Button>
                                    ) : (
                                        <Space size={8} wrap>
                                            <Button 
                                                type="primary" 
                                                icon={<SaveOutlined />}
                                                onClick={handleSaveOrder}
                                                loading={isSavingOrder}
                                                size="middle"
                                                style={{
                                                    borderRadius: '6px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                <span className="lot-ordering-button-text">Save Order</span>
                                            </Button>
                                            <Button 
                                                icon={<CloseOutlined />}
                                                onClick={handleCancelReorder}
                                                disabled={isSavingOrder}
                                                size="middle"
                                                style={{
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                <span className="lot-ordering-button-text">Cancel</span>
                                            </Button>
                                        </Space>
                                    )}
                                </div>
                            </div>
                        </Card>
                    )}

                    {!singleAuctionId ? (
                        <Card
                            size="small"
                            style={{
                                marginBottom: 16,
                                background: 'linear-gradient(to right, #e6f7ff, #f0f8ff)',
                                borderRadius: '8px',
                                border: '1px solid #91d5ff'
                            }}
                        >
                            <div style={{
                                padding: '40px 20px',
                                textAlign: 'center'
                            }}>
                                <SelectOutlined style={{
                                    fontSize: '48px',
                                    color: '#1890ff',
                                    marginBottom: '16px'
                                }} />
                                <div>
                                    <Text strong style={{
                                        fontSize: '18px',
                                        color: '#1890ff',
                                        display: 'block',
                                        marginBottom: '8px'
                                    }}>
                                        Select an Auction
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: '14px' }}>
                                        Choose an auction from the "Filter by Auction" dropdown above to view and manage its lots
                                    </Text>
                                </div>
                            </div>
                        </Card>
                    ) : isReorderMode ? (
                        renderDraggableTable()
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <Table
                                columns={filteredColumns}
                                dataSource={filteredLotsForDisplay}
                                rowKey="id"
                                loading={loading}
                                pagination={{
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} of ${total} lot${total !== 1 ? 's' : ''}`,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                    defaultPageSize: 10
                                }}
                                onChange={handleTableChange}
                                scroll={{ x: 1200 }}
                                size="middle"
                                locale={{
                                    emptyText: loading
                                        ? null // Don't show empty text when loading
                                        : (hasActiveSearch
                                            ? 'No lots match your search criteria'
                                            : 'No lots found')
                                }}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Duplicate Lot Modal */}
            <DuplicateLotModal
                open={duplicateLotModalVisible}
                title={duplicateLotTitle}
                onTitleChange={setDuplicateLotTitle}
                onCancel={() => {
                    if (duplicateLotSubmitting) return;
                    setDuplicateLotModalVisible(false);
                    setLotToDuplicate(null);
                    setDuplicateLotTitle('');
                }}
                onConfirm={handleDuplicateLot}
                loading={duplicateLotSubmitting}
            />

            {/* Create Lot Modal */}
            <Modal
                title={null}
                open={isCreateLotModalVisible}
                onCancel={handleCancelCreateLot}
                footer={null}
                centered
                width="90%"
                style={{ maxWidth: '600px', padding: 0 }}
                closable={true}
                maskClosable={true}
                className="create-lot-modal"
            >
                <div style={{ 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    {/* Header Section */}
                    <div className="create-lot-modal-header" style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        padding: '20px 24px 16px 24px',
                        textAlign: 'center',
                        borderBottom: 'none'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 12px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                            <PlusOutlined style={{ fontSize: '24px', color: 'white' }} />
                        </div>
                        <Title level={4} style={{ 
                            color: 'white', 
                            margin: 0,
                            fontWeight: 600,
                            fontSize: '20px'
                        }}>
                            Create New Lot
                        </Title>
                        <span style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '13px',
                            display: 'block',
                            marginTop: '6px'
                        }}>
                            Choose your preferred method to add lots to your auction
                        </span>
                    </div>

                    {/* Options Section */}
                    <div className="create-lot-modal-content" style={{ padding: '32px' }}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {/* Single Lot Option */}
                            <Card
                                hoverable
                                className="create-lot-option-card"
                                style={{ 
                                    cursor: 'pointer',
                                    border: '2px solid #e6f7ff',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                                onClick={handleCreateSingleLot}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#1890ff';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(24, 144, 255, 0.2)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#e6f7ff';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                bodyStyle={{ padding: '24px' }}
                            >
                                <div className="create-lot-option-content" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                    <div className="create-lot-option-icon" style={{
                                        width: '56px',
                                        height: '56px',
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                    }}>
                                        <FileTextOutlined style={{ fontSize: '28px', color: 'white' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ 
                                            marginBottom: '8px'
                                        }}>
                                            <span style={{ 
                                                fontSize: '18px', 
                                                color: '#262626',
                                                fontWeight: 600,
                                                display: 'block',
                                                marginBottom: '6px'
                                            }}>
                                                Create Single Lot
                                            </span>
                                            <div style={{ display: 'flex' }}>
                                            <Tag color="blue" style={{ 
                                                margin: 0,
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                padding: '2px 8px'
                                            }}>
                                                Recommended
                                            </Tag>
                                        </div>
                                        </div>
                                        <p style={{ 
                                            fontSize: '14px',
                                            lineHeight: '1.6',
                                            color: '#595959',
                                            margin: 0,
                                            marginBottom: '12px'
                                        }}>
                                            Create one lot with detailed information, images, specifications, and comprehensive lot details
                                        </p>
                                        <div className="create-lot-features" style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '16px',
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                ✓ Detailed forms
                                            </span>
                                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                ✓ Image uploads
                                            </span>
                                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                ✓ Full control
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            {/* Bulk Upload Option */}
                            <Card
                                hoverable
                                className="create-lot-option-card"
                                style={{ 
                                    cursor: 'pointer',
                                    border: '2px solid #f6ffed',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f6ffed 100%)',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                                onClick={handleBulkUpload}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#52c41a';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(82, 196, 26, 0.2)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#f6ffed';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                bodyStyle={{ padding: '24px' }}
                            >
                                <div className="create-lot-option-content" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                    <div className="create-lot-option-icon" style={{
                                        width: '56px',
                                        height: '56px',
                                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)'
                                    }}>
                                        <UploadOutlined style={{ fontSize: '28px', color: 'white' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ 
                                            marginBottom: '8px'
                                        }}>
                                            <span style={{ 
                                                fontSize: '18px', 
                                                color: '#262626',
                                                fontWeight: 600,
                                                display: 'block',
                                                marginBottom: '6px'
                                            }}>
                                                Bulk Upload via CSV
                                            </span>
                                            <div style={{ display: 'flex' }}>
                                            <Tag color="green" style={{ 
                                                margin: 0,
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                padding: '2px 8px'
                                            }}>
                                                Fast
                                            </Tag>
                                        </div>
                                        </div>
                                        <p style={{ 
                                            fontSize: '14px',
                                            lineHeight: '1.6',
                                            color: '#595959',
                                            margin: 0,
                                            marginBottom: '12px'
                                        }}>
                                            Upload multiple lots at once using a CSV file. Perfect for adding many lots quickly with structured data
                                        </p>
                                        <div className="create-lot-features" style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '16px',
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                ✓ CSV format
                                            </span>
                                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                ✓ Multiple lots
                                            </span>
                                            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                ✓ Time saving
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Space>

                        {/* Footer Actions */}
                        <div className="create-lot-modal-footer" style={{ 
                            marginTop: '32px',
                            paddingTop: '24px',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px'
                        }}>
                            <Button 
                                onClick={handleCancelCreateLot}
                                size="large"
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    padding: '0 24px',
                                    height: '40px'
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Auction Selection Modal */}
            <Modal
                title={null}
                open={isAuctionSelectionModalVisible}
                onCancel={handleCancelAuctionSelection}
                footer={null}
                centered
                width={550}
                closable={true}
                maskClosable={true}
                className="auction-selection-modal"
                style={{ padding: 0 }}
            >
                <div style={{ 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    {/* Header Section */}
                    <div style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        padding: '20px 24px 16px 24px',
                        textAlign: 'center',
                        borderBottom: 'none'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 12px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                            {lotCreationType === 'bulk' ? (
                                <UploadOutlined style={{ fontSize: '24px', color: 'white' }} />
                            ) : (
                                <SelectOutlined style={{ fontSize: '24px', color: 'white' }} />
                            )}
                        </div>
                        <Title level={4} style={{ 
                            color: 'white', 
                            margin: 0,
                            fontWeight: 600,
                            fontSize: '20px'
                        }}>
                            Select Auction
                        </Title>
                        <Text style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '13px',
                            display: 'block',
                            marginTop: '6px'
                        }}>
                            {lotCreationType === 'bulk' 
                                ? 'Choose the auction where you want to upload your lots'
                                : 'Choose the auction where you want to add your lot'}
                        </Text>
                    </div>

                    {/* Selection Section */}
                    <div style={{ padding: '24px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <Text strong style={{ 
                                fontSize: '14px', 
                                color: '#262626', 
                                fontWeight: 500,
                                display: 'block',
                                marginBottom: '12px'
                            }}>
                                Select Auction
                            </Text>
                            <Select
                                style={{ width: '100%', borderRadius: '8px' }}
                                value={selectedAuctionForLot}
                                onChange={handleAuctionSelectionForLot}
                                placeholder="Select an auction..."
                                allowClear
                                showSearch
                                size="large"
                                notFoundContent="No auctions found"
                                filterOption={(input, option) => {
                                    const auctionId = option.value;
                                    const auction = auctions.find(a => a.id === auctionId);
                                    
                                    if (!auction) return false;
                                    
                                    const searchText = input.toLowerCase();
                                    const auctionTitle = auction.title?.toLowerCase() || '';
                                    const statusLabel = getAuctionStatusLabel(auction.status)?.toLowerCase() || '';
                                    
                                    return (
                                        auctionTitle.includes(searchText) ||
                                        statusLabel.includes(searchText)
                                    );
                                }}
                                optionLabelProp="label"
                            >
                                {auctions
                                    .filter(auction => auction.status !== 'completed' && auction.status !== 'cancelled')
                                    .map(auction => (
                                        <Option 
                                            key={auction.id} 
                                            value={auction.id}
                                            label={
                                                <Space>
                                                    <span>{auction.title}</span>
                                                    <Tag color={getAuctionStatusColor(auction.status)}>
                                                        {getAuctionStatusLabel(auction.status)}
                                                    </Tag>
                                                </Space>
                                            }
                                        >
                                            <Space>
                                                <span>{auction.title}</span>
                                                <Tag color={getAuctionStatusColor(auction.status)}>
                                                    {getAuctionStatusLabel(auction.status)}
                                                </Tag>
                                            </Space>
                                        </Option>
                                    ))}
                            </Select>
                        </div>

                        {/* Selected Auction Info */}
                        {selectedAuctionForLot && (() => {
                            const selectedAuctionObj = auctions.find(a => a.id === selectedAuctionForLot);
                            if (!selectedAuctionObj) return null;
                            
                            return (
                                <div style={{ 
                                    marginBottom: '24px',
                                    padding: '16px',
                                    background: '#f0f8ff',
                                    borderRadius: '8px',
                                    border: '1px solid #d6e4ff'
                                }}>
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                            Selected Auction:
                                        </span>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <span style={{ fontSize: '14px', color: '#1890ff', fontWeight: 600, display: 'block' }}>
                                                {selectedAuctionObj.title}
                                            </span>
                                            <div>
                                            <Tag 
                                                color={getAuctionStatusColor(selectedAuctionObj.status)} 
                                                style={{ fontSize: '11px', margin: 0 }}
                                            >
                                                {getAuctionStatusLabel(selectedAuctionObj.status)}
                                            </Tag>
                                            </div>
                                        </div>
                                    </Space>
                                </div>
                            );
                        })()}

                        {/* Footer Actions */}
                        <div style={{ 
                            marginTop: '24px',
                            paddingTop: '20px',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px'
                        }}>
                            <Button 
                                onClick={handleCancelAuctionSelection}
                                size="large"
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    padding: '0 24px',
                                    height: '40px'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="primary"
                                onClick={handleProceedToCreateLot}
                                disabled={!selectedAuctionForLot}
                                size="large"
                                icon={lotCreationType === 'bulk' ? <UploadOutlined /> : <PlusOutlined />}
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    padding: '0 24px',
                                    height: '40px',
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    border: 'none'
                                }}
                            >
                                {lotCreationType === 'bulk' ? 'Next' : 'Create Lot'}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Responsive Styles */}
            <style>{`
                /* Disabled button styling for this component */
                .ant-btn-primary:disabled,
                .ant-btn-primary[disabled],
                .ant-btn-primary.ant-btn-disabled {
                    color: white !important;
                    background: rgba(0, 0, 0, 0.25) !important;
                    border-color: #d9d9d9 !important;
                    cursor: not-allowed;
                }
                
                .ant-btn-primary:disabled .anticon,
                .ant-btn-primary[disabled] .anticon,
                .ant-btn-primary.ant-btn-disabled .anticon {
                    color: white !important;
                }
                
                .create-lot-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }

          
                .create-lot-modal .ant-modal-close {
                       display: flex;
    font-size: 16px;
    font-style: normal;
    line-height: 32px;
    justify-content: center;
    text-transform: none;
    text-rendering: auto;
    color: #ffffff;
    padding: 10px;
    background: #333333;
    border-radius: 50%;
    width: 30px;
    height: 30px;
                }
                
                .create-lot-modal .ant-modal-close:hover {
                    color: white;
                }
                
                .create-lot-option-card {
                    will-change: transform;
                }
                
                .create-lot-option-card:hover {
                    cursor: pointer;
                }
                
                .auction-selection-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .auction-selection-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                }
                
                .auction-selection-modal .ant-modal-close:hover {
                    color: white;
                }
                
                .auction-selection-modal .ant-btn-primary:disabled,
                .auction-selection-modal .ant-btn-primary[disabled],
                .auction-selection-modal .ant-btn-primary.ant-btn-disabled {
                    color: white !important;
                    background: rgba(0, 0, 0, 0.25) !important;
                    border-color: #d9d9d9 !important;
                    cursor: not-allowed;
                }
                
                .auction-selection-modal .ant-btn-primary:disabled .anticon,
                .auction-selection-modal .ant-btn-primary[disabled] .anticon,
                .auction-selection-modal .ant-btn-primary.ant-btn-disabled .anticon {
                    color: white !important;
                }
                
                @media (max-width: 767px) {
                    .create-lot-modal .ant-modal {
                        width: 95% !important;
                        max-width: 95% !important;
                        margin: 10px auto !important;
                    }
                    
                    .create-lot-modal-header {
                        padding: 16px 20px 12px 20px !important;
                    }
                    
                    .create-lot-modal-header h4.ant-typography {
                        font-size: 18px !important;
                    }
                    
                    .create-lot-modal-header span {
                        font-size: 12px !important;
                    }
                    
                    .create-lot-modal-content {
                        padding: 20px 16px !important;
                    }
                    
                    .create-lot-option-card .ant-card-body {
                        padding: 16px !important;
                    }
                    
                    .create-lot-option-content {
                        flex-direction: column !important;
                        gap: 12px !important;
                    }
                    
                    .create-lot-option-icon {
                        align-self: center !important;
                        width: 48px !important;
                        height: 48px !important;
                    }
                    
                    .create-lot-option-icon .anticon {
                        font-size: 24px !important;
                    }
                    
                    .create-lot-option-content > div:last-child {
                        text-align: center;
                    }
                    
                    .create-lot-option-content > div:last-child > div:first-child {
                        justify-content: center !important;
                    }
                    
                    .create-lot-option-content > div:last-child > div:first-child > div:last-child {
                        justify-content: center !important;
                    }
                    
                    .create-lot-option-content > div:last-child > div:last-child {
                        justify-content: center !important;
                    }
                    
                    .create-lot-features {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 8px !important;
                    }
                    
                    .create-lot-features span {
                        font-size: 11px !important;
                        display: block !important;
                        width: 100% !important;
                    }
                    
                    .create-lot-modal-footer {
                        flex-direction: column !important;
                        gap: 8px !important;
                    }
                    
                    .create-lot-modal-footer .ant-btn {
                        width: 100% !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .create-lot-modal .ant-modal {
                        width: 98% !important;
                        max-width: 98% !important;
                    }
                    
                    .create-lot-modal-header {
                        padding: 12px 16px 10px 16px !important;
                    }
                    
                    .create-lot-modal-header h4.ant-typography {
                        font-size: 16px !important;
                    }
                    
                    .create-lot-modal-content {
                        padding: 16px 12px !important;
                    }
                    
                    .create-lot-option-card .ant-card-body {
                        padding: 12px !important;
                    }
                    
                    .create-lot-option-content > div:last-child span {
                        font-size: 16px !important;
                    }
                    
                    .create-lot-option-content > div:last-child p {
                        font-size: 13px !important;
                    }
                    
                    .create-lot-features span {
                        font-size: 10px !important;
                    }
                }
                
                @media (max-width: 767px) {
                    .selected-auction-info {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 8px !important;
                    }
                    
                    .selected-auction-info > div:first-child {
                        width: 100% !important;
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 8px !important;
                    }
                    
                    .selected-auction-button {
                        align-self: flex-start !important;
                        padding: 4px 0 !important;
                    }
                    
                    .selected-auction-button-text {
                        display: inline-block;
                    }

                    /* Lot Ordering Responsive */
                    .lot-ordering-container {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 12px !important;
                    }
                    
                    .lot-ordering-actions {
                        width: 100% !important;
                        justify-content: flex-start !important;
                    }
                    
                    .lot-ordering-actions .ant-space {
                        width: 100% !important;
                        display: flex !important;
                    }
                    
                    .lot-ordering-actions .ant-space-item {
                        flex: 1 !important;
                    }
                    
                    .lot-ordering-actions .ant-btn {
                        width: 100% !important;
                    }
                    
                    .lot-ordering-description {
                        font-size: 11px !important;
                    }
                }
                
                @media (max-width: 575px) {
                    .selected-auction-info {
                        padding: 10px 12px !important;
                    }

                    .lot-ordering-card .ant-card-body {
                        padding: 10px 12px !important;
                    }
                    
                    .lot-ordering-container {
                        padding: 8px 0 !important;
                    }
                    
                    .lot-ordering-button-text {
                        font-size: 12px !important;
                    }
                }
            `}</style>
        </DashboardLayout>
    );
};

export default MyLots; 